import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Heart, Plus, Minus, Truck, RefreshCw, Sparkles, Star } from "lucide-react";
import api, { formatGBP, formatApiError } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import ProductCard from "../components/ProductCard";
import Seo from "../components/Seo";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const { addToCart, toggleWishlist, inWishlist } = useCart();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [aiRecs, setAiRecs] = useState({ products: [] });
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: "", body: "" });
  const [zoom, setZoom] = useState({ on: false, x: 50, y: 50 });
  const wrapRef = useRef();

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${id}`).then(({ data }) => setP(data));
    api.post("/ai/recommend", { product_id: id }).then(({ data }) => setAiRecs(data)).catch(() => {});
  }, [id]);

  if (!p) return <div className="max-w-[1480px] mx-auto px-6 md:px-12 py-32 text-center text-neutral-500">Loading…</div>;

  const stockState = p.stock <= 0 ? "out" : p.stock < 5 ? "low" : "in";
  const wished = inWishlist(p.id);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Please sign in to leave a review");
    try {
      const { data } = await api.post("/reviews", { product_id: p.id, ...reviewForm });
      setP({ ...p, reviews: [data, ...(p.reviews || [])] });
      setReviewForm({ rating: 5, title: "", body: "" });
      toast.success("Thank you for your review");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onMove = (e) => {
    if (!zoom.on) return;
    const r = wrapRef.current.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    setZoom({ ...zoom, x, y });
  };

  return (
    <main className="max-w-[1480px] mx-auto px-6 md:px-12 py-12 md:py-20" data-testid="pdp">
      <Seo title={p.name}
           description={p.description?.slice(0, 160)}
           image={p.images?.[0]} type="product"
           jsonLd={{
             "@context": "https://schema.org", "@type": "Product",
             "name": p.name, "description": p.description, "image": p.images,
             "sku": p.sku, "brand": { "@type": "Brand", "name": "Aura & Co." },
             "offers": {
               "@type": "Offer", "priceCurrency": "GBP", "price": p.price,
               "availability": p.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
               "url": typeof window !== 'undefined' ? window.location.href : ""
             },
             ...(p.avg_rating > 0 ? { "aggregateRating": { "@type": "AggregateRating", "ratingValue": p.avg_rating, "reviewCount": p.reviews?.length || 0 } } : {})
           }} />
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
        {/* Gallery */}
        <div>
          <div ref={wrapRef}
            className={`zoom-wrap aspect-[4/5] bg-sand ${zoom.on ? "zoomed" : ""}`}
            onMouseMove={onMove}
            onClick={() => setZoom((z) => ({ ...z, on: !z.on }))}
            data-testid="pdp-main-image">
            <img src={p.images?.[activeImg]} alt={p.name}
              className="zoom-img w-full h-full object-cover"
              style={zoom.on ? { transformOrigin: `${zoom.x}% ${zoom.y}%` } : {}} />
          </div>
          {p.images?.length > 1 && (
            <div className="grid grid-cols-4 gap-3 mt-4" data-testid="pdp-thumbs">
              {p.images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)}
                  className={`aspect-square bg-sand overflow-hidden ${activeImg === i ? "ring-2 ring-[var(--gold)]" : ""}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div className="lg:sticky lg:top-32 lg:self-start">
          <p className="eyebrow text-neutral-500">{p.category}</p>
          <h1 className="font-serif text-4xl md:text-5xl mt-2" data-testid="pdp-name">{p.name}</h1>

          <div className="flex items-center gap-3 mt-4">
            {p.avg_rating > 0 && (
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map((n) => <Star key={n} className={`w-4 h-4 ${n <= Math.round(p.avg_rating) ? "fill-[var(--gold)] text-[var(--gold)]" : "text-neutral-300"}`} strokeWidth={1.25} />)}
                <span className="text-xs text-neutral-500 ml-1">({p.reviews?.length || 0})</span>
              </div>
            )}
            <span className="text-xs text-neutral-500">SKU: {p.sku}</span>
          </div>

          <p className="font-serif text-3xl mt-6" data-testid="pdp-price">{formatGBP(p.price)}</p>
          <p className="text-base font-light text-neutral-600 mt-6 leading-relaxed">{p.description}</p>
          {p.material && <p className="text-sm text-neutral-500 mt-3"><span className="eyebrow text-neutral-700 mr-2">Material</span>{p.material}</p>}

          <div className="rule my-8" />

          <div className="flex items-center gap-4">
            <div className="flex items-center border border-neutral-900">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-3" data-testid="pdp-qty-dec"><Minus className="w-3 h-3" strokeWidth={1.25} /></button>
              <span className="px-4 text-sm" data-testid="pdp-qty">{qty}</span>
              <button onClick={() => setQty(Math.min(p.stock || 99, qty + 1))} className="px-4 py-3" data-testid="pdp-qty-inc"><Plus className="w-3 h-3" strokeWidth={1.25} /></button>
            </div>
            <button onClick={() => addToCart(p, qty)} disabled={stockState === "out"} className="btn-primary flex-1" data-testid="pdp-add-cart">
              {stockState === "out" ? "Sold Out" : "Add to Bag"}
            </button>
            <button onClick={() => toggleWishlist(p)} aria-label="Wishlist" data-testid="pdp-wishlist"
              className={`p-3 border border-neutral-900 transition-colors ${wished ? "bg-neutral-900 text-white" : ""}`}>
              <Heart strokeWidth={1.25} fill={wished ? "currentColor" : "none"} />
            </button>
          </div>
          <p className={`text-sm mt-4 ${stockState === "low" ? "text-[var(--gold)]" : "text-neutral-500"}`} data-testid="pdp-stock">
            {stockState === "out" ? "Currently sold out — join the waitlist" :
             stockState === "low" ? `Only ${p.stock} left in stock` :
             "In stock — ready to ship"}
          </p>

          <div className="grid grid-cols-2 gap-4 mt-10 text-sm">
            <div className="border border-hair p-5 flex gap-3"><Truck className="w-5 h-5 text-[var(--gold)]" strokeWidth={1.25} /><div><p className="font-medium">Free UK delivery</p><p className="text-neutral-500 text-xs mt-1">On orders over £100</p></div></div>
            <div className="border border-hair p-5 flex gap-3"><RefreshCw className="w-5 h-5 text-[var(--gold)]" strokeWidth={1.25} /><div><p className="font-medium">30-day returns</p><p className="text-neutral-500 text-xs mt-1">Complimentary</p></div></div>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section className="mt-24 grid lg:grid-cols-2 gap-16" data-testid="reviews-section">
        <div>
          <p className="eyebrow text-neutral-600">Words from her wearers</p>
          <h2 className="font-serif text-3xl md:text-4xl mt-2 mb-8">Reviews</h2>
          {(p.reviews || []).length === 0 ? (
            <p className="text-neutral-500 text-sm">No reviews yet — be the first to share yours.</p>
          ) : (
            <div className="space-y-8">
              {p.reviews.map((r) => (
                <div key={r.id} className="border-b border-hair pb-8" data-testid={`review-${r.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {[1,2,3,4,5].map((n) => <Star key={n} className={`w-3 h-3 ${n <= r.rating ? "fill-[var(--gold)] text-[var(--gold)]" : "text-neutral-300"}`} strokeWidth={1.25} />)}
                    <span className="text-xs uppercase tracking-[0.18em] text-neutral-500 ml-2">{r.user_name}</span>
                  </div>
                  <p className="font-serif text-lg">{r.title}</p>
                  <p className="text-sm text-neutral-600 mt-2">{r.body}</p>
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <p className="eyebrow text-neutral-600">Share yours</p>
          <h3 className="font-serif text-2xl mt-2 mb-6">Write a review</h3>
          {!user ? (
            <p className="text-sm text-neutral-500">Please <a href="/login" className="underline">sign in</a> to leave a review.</p>
          ) : (
            <form onSubmit={submitReview} className="space-y-4" data-testid="review-form">
              <div className="flex gap-1">
                {[1,2,3,4,5].map((n) => (
                  <button type="button" key={n} onClick={() => setReviewForm({ ...reviewForm, rating: n })}>
                    <Star className={`w-6 h-6 ${n <= reviewForm.rating ? "fill-[var(--gold)] text-[var(--gold)]" : "text-neutral-300"}`} strokeWidth={1.25} />
                  </button>
                ))}
              </div>
              <input required value={reviewForm.title} onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                placeholder="Title" data-testid="review-title" className="w-full bg-cream border border-hair px-4 py-3 text-sm outline-none focus:border-[var(--gold)]" />
              <textarea required rows={5} value={reviewForm.body} onChange={(e) => setReviewForm({ ...reviewForm, body: e.target.value })}
                placeholder="Your thoughts" data-testid="review-body" className="w-full bg-cream border border-hair px-4 py-3 text-sm outline-none focus:border-[var(--gold)]" />
              <button className="btn-primary" data-testid="review-submit">Submit Review</button>
            </form>
          )}
        </div>
      </section>

      {/* Related */}
      {aiRecs.products?.length > 0 && (
        <section className="mt-24" data-testid="related-section">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-[var(--gold)]" strokeWidth={1.25} />
            <p className="eyebrow text-neutral-600">Curated for this piece</p>
          </div>
          <h2 className="font-serif text-3xl md:text-4xl mb-10">{aiRecs.reason}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
            {aiRecs.products.filter((x) => x.id !== p.id).slice(0, 4).map((rp, i) => <ProductCard key={rp.id} product={rp} index={i} />)}
          </div>
        </section>
      )}
    </main>
  );
}
